﻿import React, { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { Tab, TabGroup, TabList, TabPanel, TabPanels } from '@headlessui/react'
import clsx from 'clsx'
import { Text, Heading, Button } from '../components/ui'
import { PermissionGuard } from '../components/PermissionGuard'
import { usePermissions, usePermission } from '../hooks/usePermissions'
import {
  DocumentTextIcon,
  XMarkIcon,
  CalendarIcon,
  HomeIcon,
  RssIcon,
  ArrowPathIcon,
  PuzzlePieceIcon,
} from '@heroicons/react/24/outline'
import { BRAND_COLOR } from '../constants'
import { supabase } from '../lib/supabase'
import { getVersions } from '../services/informativo'
import { socialService } from '../services/social'
import { marketingService } from '../services/marketing'
import { InformativoVersion } from '../types/informativo'
import { NotificationRecipientItem } from '../services/notifications'
import { useNotifications } from '../hooks/useNotifications'
import { qgNumerosService } from '../services/qgNumeros'
import { QgNumerosRow } from '../types/qgNumeros'
import PdfModal from '../components/PdfModal'
import UserConfigModal from '../components/UserConfigModal'
import { getSignedAvatarUrl } from '../services/avatar'
import NotificationDetailModal from '../components/notifications/NotificationDetailModal'
import {
  CirculandoHomeSection,
  DashboardSectionDivider,
  DashboardWelcomeHeader,
  DashboardOrientacaoSection,
  InformativosHub,
  NotificationsPanel,
  QGNumerosModal,
  SocialFeed,
  HomeCacaPalavrasSection,
} from '../components/dashboard'
import HomePopup from '../components/dashboard/HomePopup'
import type { SocialFeedToolbarState } from '../components/dashboard/social/SocialFeed'

const isImageFilePath = (filePath: string | null | undefined): boolean =>
  Boolean(filePath && /\.(jpe?g|png|gif|webp)$/i.test(filePath))

const dashboardMainTabClass = (active: boolean) =>
  clsx(
    'relative -mb-[3px] inline-flex cursor-pointer items-center gap-2 rounded-t-lg border-b-[3px] px-4 py-3 text-base font-semibold transition-colors',
    'outline-none focus:outline-none data-[focus]:outline-none data-[focus]:ring-2 data-[focus]:ring-inset data-[focus]:ring-primary-500/40',
    active
      ? 'border-primary-500 bg-primary-500 text-white shadow-sm'
      : 'border-transparent text-gray-500 hover:border-primary-500 hover:bg-primary-50 hover:text-primary-600 dark:text-gray-400 dark:hover:border-primary-400 dark:hover:bg-primary-500/10 dark:hover:text-primary-300'
  )

const Dashboard: React.FC = () => {
  const navigate = useNavigate()
  const { loading, error } = usePermissions()
  const { hasPermission: canViewQgNumeros, loading: loadingViewValues } = usePermission(
    'dashboard',
    'view_values'
  )
  const { hasPermission: showSocialFeed } = usePermission('social', 'show')
  const { items: notifications, loading: notificationsLoading, markAsRead, deleteOne } = useNotifications()
  const showLatestNotifications = notifications.length > 0
  const [homeTabIndex, setHomeTabIndex] = useState(0)
  const [feedToolbar, setFeedToolbar] = useState<SocialFeedToolbarState | null>(null)
  const [feedPostsCount, setFeedPostsCount] = useState<number | null>(null)
  const [hasCacaPalavrasGames, setHasCacaPalavrasGames] = useState(false)
  const [socialComposerPrefill, setSocialComposerPrefill] = useState<string | null>(null)
  const [socialComposerPrefillKey, setSocialComposerPrefillKey] = useState(0)

  useEffect(() => {
    if (!showSocialFeed) {
      setFeedPostsCount(null)
      return
    }

    let cancelled = false
    socialService
      .listPosts()
      .then((posts) => {
        if (!cancelled) setFeedPostsCount(posts.length)
      })
      .catch(() => {
        if (!cancelled) setFeedPostsCount(null)
      })

    return () => {
      cancelled = true
    }
  }, [showSocialFeed])

  useEffect(() => {
    if (loading) return

    let cancelled = false
    marketingService
      .hasGames()
      .then((exists) => {
        if (!cancelled) setHasCacaPalavrasGames(exists)
      })
      .catch(() => {
        if (!cancelled) setHasCacaPalavrasGames(false)
      })

    return () => {
      cancelled = true
    }
  }, [loading])

  useEffect(() => {
    if (!hasCacaPalavrasGames && homeTabIndex === 2) {
      setHomeTabIndex(0)
    }
  }, [hasCacaPalavrasGames, homeTabIndex])

  const handleFeedToolbarChange = useCallback((state: SocialFeedToolbarState | null) => {
    setFeedToolbar(state)
    if (state) setFeedPostsCount(state.postsCount)
  }, [])

  const handleShareGameResult = useCallback((text: string) => {
    setSocialComposerPrefill(text)
    setSocialComposerPrefillKey((n) => n + 1)
    setHomeTabIndex(1)
  }, [])

  // Estados para versões do Circulando
  const [circulandoVersions, setCirculandoVersions] = useState<InformativoVersion[]>([])
  const [circulandoVersionsLoading, setCirculandoVersionsLoading] = useState<boolean>(true)
  const [circulandoVersionsError, setCirculandoVersionsError] = useState<string | null>(null)

  // Estados para versões da Imprensa
  const [imprensaVersions, setImprensaVersions] = useState<InformativoVersion[]>([])
  const [imprensaVersionsLoading, setImprensaVersionsLoading] = useState<boolean>(true)
  const [imprensaVersionsError, setImprensaVersionsError] = useState<string | null>(null)

  // Estados para versões Internas
  const [internoVersions, setInternoVersions] = useState<InformativoVersion[]>([])
  const [internoVersionsLoading, setInternoVersionsLoading] = useState<boolean>(true)
  const [internoVersionsError, setInternoVersionsError] = useState<string | null>(null)

  // Estados para versões do Boletim
  const [boletimVersions, setBoletimVersions] = useState<InformativoVersion[]>([])
  const [boletimVersionsLoading, setBoletimVersionsLoading] = useState<boolean>(true)
  const [boletimVersionsError, setBoletimVersionsError] = useState<string | null>(null)

  const [notificationSendersMap, setNotificationSendersMap] = useState<Record<string, { name: string; avatarUrl: string | null }>>({})

  // Estados para "Números do PASI QG"
  const [qgNumerosData, setQgNumerosData] = useState<QgNumerosRow | null>(null)
  const [qgNumerosLoading, setQgNumerosLoading] = useState<boolean>(true)
  const [qgNumerosError, setQgNumerosError] = useState<string | null>(null)

  // Estado para modal de configurações do usuário (atalho na seção Orientação)
  const [isUserConfigModalOpen, setIsUserConfigModalOpen] = useState(false)
  const [isQgNumerosModalOpen, setIsQgNumerosModalOpen] = useState(false)
  
  // Estado para modal de notificação completa
  const [selectedNotification, setSelectedNotification] = useState<NotificationRecipientItem | null>(null)
  const [senderInfo, setSenderInfo] = useState<{ name: string; avatarUrl: string | null } | null>(null)
  const [loadingAvatar, setLoadingAvatar] = useState<boolean>(false)

  const handleNotificationInternalNavigate = useCallback((path: string) => {
    setSelectedNotification(null)
    navigate(path)
  }, [navigate])

  // Estado para modal de PDF / imagem
  const [showPdfModal, setShowPdfModal] = useState<boolean>(false)
  const [pdfModalLarge, setPdfModalLarge] = useState(false)
  const [pdfUrl, setPdfUrl] = useState<string | null>(null)
  const [pdfTitle, setPdfTitle] = useState<string>('Visualizar arquivo')
  const [showImageModal, setShowImageModal] = useState<boolean>(false)
  const [imageUrl, setImageUrl] = useState<string | null>(null)
  const [imageTitle, setImageTitle] = useState<string>('Visualizar imagem')

  // Estado para modal de detalhe (Interno / Boletim)
  const [showDetailModal, setShowDetailModal] = useState<boolean>(false)
  const [selectedDetailVersion, setSelectedDetailVersion] = useState<InformativoVersion | null>(null)
  const [detailKind, setDetailKind] = useState<'interno' | 'boletim'>('interno')

  // Função para gerar inicial do nome
  const getInitials = (text: string): string => {
    if (!text) return 'N'
    const words = text.trim().split(/\s+/)
    if (words.length >= 2) {
      return (words[0][0] + words[1][0]).toUpperCase()
    }
    return text.substring(0, 2).toUpperCase()
  }

  // Função para gerar cor do avatar
  const getAvatarColor = (text: string): string => {
    const colors = [
      'bg-purple-500',
      'bg-primary-500',
      'bg-green-500',
      'bg-yellow-500',
      'bg-pink-500',
      'bg-indigo-500',
      'bg-red-500',
      'bg-teal-500',
    ]
    let hash = 0
    for (let i = 0; i < text.length; i++) {
      hash = text.charCodeAt(i) + ((hash << 5) - hash)
    }
    return colors[Math.abs(hash) % colors.length]
  }

  // Resetar estados quando uma nova notificação é selecionada
  useEffect(() => {
    if (selectedNotification) {
      setSenderInfo(null)
      setLoadingAvatar(true)
    }
  }, [selectedNotification?.id])

  // Buscar informações do usuário que criou a notificação
  useEffect(() => {
    const fetchSenderInfo = async () => {
      if (!selectedNotification?.notification?.created_by) {
        setSenderInfo(null)
        setLoadingAvatar(false)
        return
      }

      setLoadingAvatar(true)
      try {
        // Buscar perfil do usuário
        const { data: profile, error } = await supabase
          .from('profiles')
          .select('name, avatar_url')
          .eq('id', selectedNotification.notification.created_by)
          .single()

        if (error || !profile) {
          setSenderInfo({ name: 'Usuário', avatarUrl: null })
          setLoadingAvatar(false)
          return
        }

        // Buscar URL assinada do avatar se existir
        let avatarUrl: string | null = null
        if (profile.avatar_url) {
          avatarUrl = await getSignedAvatarUrl(profile.avatar_url)
        }

        setSenderInfo({
          name: profile.name || 'Usuário',
          avatarUrl
        })
      } catch (err) {
        console.error('Erro ao buscar informações do remetente:', err)
        setSenderInfo({ name: 'Usuário', avatarUrl: null })
      } finally {
        setLoadingAvatar(false)
      }
    }

    fetchSenderInfo()
  }, [selectedNotification])

  // Carregar versões do Circulando
  useEffect(() => {
    // Aguarda o carregamento das permissões antes de buscar dados
    if (loading) {
      return
    }

    const loadCirculandoVersions = async () => {
      try {
        setCirculandoVersionsLoading(true)
        setCirculandoVersionsError(null)
        const versions = await getVersions('circulando')
        setCirculandoVersions(versions)
      } catch (e: any) {
        setCirculandoVersionsError(e?.message || 'Erro ao carregar versões do Circulando')
        setCirculandoVersions([])
      } finally {
        setCirculandoVersionsLoading(false)
      }
    }
    loadCirculandoVersions()
  }, [loading])

  // Carregar versões da Imprensa
  useEffect(() => {
    // Aguarda o carregamento das permissões antes de buscar dados
    if (loading) {
      return
    }

    const loadImprensaVersions = async () => {
      try {
        setImprensaVersionsLoading(true)
        setImprensaVersionsError(null)
        const versions = await getVersions('imprensa')
        setImprensaVersions(versions)
      } catch (e: any) {
        setImprensaVersionsError(e?.message || 'Erro ao carregar versões da Imprensa')
        setImprensaVersions([])
      } finally {
        setImprensaVersionsLoading(false)
      }
    }
    loadImprensaVersions()
  }, [loading])

  // Carregar versões Internas
  useEffect(() => {
    // Aguarda o carregamento das permissões antes de buscar dados
    if (loading) {
      return
    }

    const loadInternoVersions = async () => {
      try {
        setInternoVersionsLoading(true)
        setInternoVersionsError(null)
        const versions = await getVersions('interno')
        setInternoVersions(versions)
      } catch (e: any) {
        setInternoVersionsError(e?.message || 'Erro ao carregar versões Internas')
        setInternoVersions([])
      } finally {
        setInternoVersionsLoading(false)
      }
    }
    loadInternoVersions()
  }, [loading])

  // Carregar versões do Boletim
  useEffect(() => {
    if (loading) {
      return
    }

    const loadBoletimVersions = async () => {
      try {
        setBoletimVersionsLoading(true)
        setBoletimVersionsError(null)
        const versions = await getVersions('boletim')
        setBoletimVersions(versions)
      } catch (e: any) {
        setBoletimVersionsError(e?.message || 'Erro ao carregar versões do Boletim')
        setBoletimVersions([])
      } finally {
        setBoletimVersionsLoading(false)
      }
    }
    loadBoletimVersions()
  }, [loading])

  // Carregar Números do PASI QG (view materializada mv_qg_dashboard_kpis)
  useEffect(() => {
    if (loading || loadingViewValues) {
      return
    }

    if (!canViewQgNumeros) {
      setQgNumerosLoading(false)
      setQgNumerosData(null)
      setQgNumerosError(null)
      return
    }

    const loadQgNumeros = async () => {
      try {
        setQgNumerosLoading(true)
        setQgNumerosError(null)
        const data = await qgNumerosService.fetchNumeros()
        setQgNumerosData(data)
      } catch (e: any) {
        setQgNumerosError(e?.message || 'Erro ao carregar Números do PASI QG')
        setQgNumerosData(null)
      } finally {
        setQgNumerosLoading(false)
      }
    }
    loadQgNumeros()
  }, [loading, loadingViewValues, canViewQgNumeros])

  // Buscar informações dos remetentes das notificações
  useEffect(() => {
    const fetchNotificationSenders = async () => {
      if (notifications.length === 0) {
        setNotificationSendersMap({})
        return
      }

      // Coletar todos os IDs únicos de remetentes
      const senderIds = Array.from(
        new Set(
          notifications
            .map(it => it.notification?.created_by)
            .filter(Boolean) as string[]
        )
      )

      if (senderIds.length === 0) {
        setNotificationSendersMap({})
        return
      }

      try {
        // Buscar perfis dos remetentes
        const { data: profiles, error } = await supabase
          .from('profiles')
          .select('id, name, avatar_url')
          .in('id', senderIds)

        if (error || !profiles) {
          setNotificationSendersMap({})
          return
        }

        // Criar mapa de remetentes com avatares
        const senders: Record<string, { name: string; avatarUrl: string | null }> = {}
        
        for (const profile of profiles) {
          let avatarUrl: string | null = null
          if (profile.avatar_url) {
            avatarUrl = await getSignedAvatarUrl(profile.avatar_url)
          }
          
          senders[profile.id] = {
            name: profile.name || 'Usuário',
            avatarUrl
          }
        }

        // Preencher com "Usuário" para IDs não encontrados
        senderIds.forEach(id => {
          if (!senders[id]) {
            senders[id] = { name: 'Usuário', avatarUrl: null }
          }
        })

        setNotificationSendersMap(senders)
      } catch (err) {
        console.error('Erro ao buscar informações dos remetentes:', err)
        setNotificationSendersMap({})
      }
    }

    fetchNotificationSenders()
  }, [notifications])

  // Função para obter URL pública do arquivo
  const getFileUrl = (filePath: string | null) => {
    if (!filePath) return null
    const { data } = supabase.storage
      .from('circulando')
      .getPublicUrl(filePath)
    return data.publicUrl
  }

  // Formata descrição para exibir HTML (formatação, imagens) ou texto com quebras
  const formatDescription = (description: string): string => {
    if (!description) return ''
    if (description.includes('<')) return description
    return description.replace(/\n/g, '<br />')
  }

  // Função para normalizar URL (adicionar protocolo se necessário)
  const normalizeUrl = (url: string): string => {
    if (!url) return url
    // Se já tem protocolo, retornar como está
    if (url.startsWith('http://') || url.startsWith('https://')) {
      return url
    }
    // Se não tem protocolo, adicionar https://
    return `https://${url}`
  }

  // Função para abrir arquivo do artigo em modal (PDF/imagem) ou nova aba (link)
  const handleVersionClick = (
    file: string | null,
    link: string | null,
    title?: string,
    large = false
  ) => {
    setPdfModalLarge(large)
    if (link) {
      // Links abrem em nova aba
      const normalizedLink = normalizeUrl(link)
      window.open(normalizedLink, '_blank')
    } else if (file) {
      const fileUrl = getFileUrl(file)
      if (!fileUrl) return

      if (isImageFilePath(file)) {
        setImageUrl(fileUrl)
        setImageTitle(title || 'Visualizar imagem')
        setShowImageModal(true)
        return
      }

      setPdfUrl(fileUrl)
      setPdfTitle(title || 'Visualizar arquivo')
      setShowPdfModal(true)
    }
  }

  const openAttachedFile = (file: string | null, title: string) => {
    setPdfModalLarge(false)
    const fileUrl = getFileUrl(file)
    if (!fileUrl || !file) return

    if (isImageFilePath(file)) {
      setImageUrl(fileUrl)
      setImageTitle(title)
      setShowDetailModal(false)
      setShowImageModal(true)
      return
    }

    setPdfUrl(fileUrl)
    setPdfTitle(title)
    setShowDetailModal(false)
    setShowPdfModal(true)
  }

  // Função para abrir modal de Informativo Interno
  const handleInternoClick = (version: InformativoVersion) => {
    setDetailKind('interno')
    setSelectedDetailVersion(version)
    setShowDetailModal(true)
  }

  // Função para abrir modal de Boletim (descrição + link)
  const handleBoletimClick = (version: InformativoVersion) => {
    setDetailKind('boletim')
    setSelectedDetailVersion(version)
    setShowDetailModal(true)
  }

  return (
    <>
      <div className="flex-1 flex flex-col overflow-x-hidden overflow-y-auto">
        <PermissionGuard 
          moduleName="dashboard" 
          permissionType="show"
          fallback={
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center">
                <Text size="xl" className="text-gray-500 dark:text-gray-400 mb-2">
                  Acesso Negado
                </Text>
                <Text size="sm" color="muted">
                  Você não tem permissão para acessar esta página.
                </Text>
              </div>
            </div>
          }
        >
            <div className="px-4 sm:px-6 lg:px-8 pb-4">
              {loading && (
                <div className="text-center py-8 mt-6">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500 mx-auto"></div>
                  <p className="mt-4 text-gray-600 dark:text-gray-400">Carregando permissões...</p>
                </div>
              )}

              {error && (
                <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 mb-6 mt-6">
                  <p className="text-red-800 dark:text-red-200">Erro ao carregar permissões: {error}</p>
                </div>
              )}

              {!loading && !error && (
                <>
                  <DashboardWelcomeHeader
                    onOpenUserSettings={() => setIsUserConfigModalOpen(true)}
                    onOpenQgNumeros={() => setIsQgNumerosModalOpen(true)}
                  />

                  <div>
                    <TabGroup selectedIndex={homeTabIndex} onChange={setHomeTabIndex}>
                      <div className="mt-4 mb-2 flex items-end justify-between gap-3 border-b-[3px] border-primary-500 dark:border-primary-400">
                        <TabList
                          className="flex flex-wrap items-end gap-1"
                          aria-label="Áreas da home"
                        >
                          <Tab className={({ selected }) => dashboardMainTabClass(selected)}>
                            <HomeIcon className="h-5 w-5 shrink-0" aria-hidden />
                            Informações Iniciais
                          </Tab>
                          <Tab className={({ selected }) => dashboardMainTabClass(selected)}>
                            <RssIcon className="h-5 w-5 shrink-0" aria-hidden />
                            Rede Social
                            {feedPostsCount != null ? ` (${feedPostsCount})` : ''}
                          </Tab>
                          {hasCacaPalavrasGames && (
                            <Tab className={({ selected }) => dashboardMainTabClass(selected)}>
                              <PuzzlePieceIcon className="h-5 w-5 shrink-0" aria-hidden />
                              Desafio Possível - Caça-Palavras
                            </Tab>
                          )}
                        </TabList>

                        {homeTabIndex === 1 && showSocialFeed && feedToolbar && (
                          <button
                            type="button"
                            onClick={feedToolbar.onRefresh}
                            disabled={feedToolbar.refreshing}
                            className="mb-2 inline-flex shrink-0 items-center gap-1.5 rounded-full border border-gray-200 dark:border-gray-600 px-3 py-1.5 text-xs font-medium text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700/50 disabled:opacity-50 transition-colors"
                          >
                            <ArrowPathIcon
                              className={clsx('h-4 w-4', feedToolbar.refreshing && 'animate-spin')}
                            />
                            {feedToolbar.refreshing ? (
                              'Atualizando…'
                            ) : (
                              <>
                                Atualizar
                                <span className="tabular-nums text-[10px] text-gray-500 dark:text-gray-400 min-w-[1.35rem] text-right">
                                  {feedToolbar.autoRefreshSeconds}s
                                </span>
                              </>
                            )}
                          </button>
                        )}
                      </div>

                      <TabPanels>
                        <TabPanel className="outline-none focus:outline-none data-[focus]:outline-none">
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-0 pb-2 sm:pb-3 items-stretch">
                            <div className="min-w-0">
                              <CirculandoHomeSection
                                versions={circulandoVersions}
                                loading={circulandoVersionsLoading}
                                error={circulandoVersionsError}
                                onVersionClick={(file, link, title) =>
                                  handleVersionClick(file, link, title, true)
                                }
                              />
                            </div>
                            <div className="min-w-0">
                              <DashboardOrientacaoSection />
                            </div>
                          </div>

                          <DashboardSectionDivider className="my-1 sm:my-1.5" />

                          <div className="grid grid-cols-1 md:grid-cols-5 gap-4 pt-0 pb-2 sm:pb-3 items-start">
                            <div
                              className={clsx(
                                'min-w-0',
                                showLatestNotifications ? 'md:col-span-3' : 'md:col-span-5'
                              )}
                            >
                              <InformativosHub
                                embedded
                                interno={{
                                  versions: internoVersions,
                                  loading: internoVersionsLoading,
                                  error: internoVersionsError,
                                }}
                                imprensa={{
                                  versions: imprensaVersions,
                                  loading: imprensaVersionsLoading,
                                  error: imprensaVersionsError,
                                }}
                                boletim={{
                                  versions: boletimVersions,
                                  loading: boletimVersionsLoading,
                                  error: boletimVersionsError,
                                }}
                                formatDescription={formatDescription}
                                onInternoClick={handleInternoClick}
                                onBoletimClick={handleBoletimClick}
                                onVersionClick={handleVersionClick}
                              />
                            </div>
                            {showLatestNotifications && (
                              <div className="min-w-0 md:col-span-2">
                                <NotificationsPanel
                                  notifications={notifications}
                                  loading={notificationsLoading}
                                  error={null}
                                  sendersMap={notificationSendersMap}
                                  onSelectNotification={setSelectedNotification}
                                  getInitials={getInitials}
                                  getAvatarColor={getAvatarColor}
                                />
                              </div>
                            )}
                          </div>
                        </TabPanel>

                        <TabPanel
                          unmount={false}
                          className="outline-none focus:outline-none data-[focus]:outline-none"
                        >
                          {showSocialFeed ? (
                            <SocialFeed
                              hideRefreshButton
                              onToolbarChange={handleFeedToolbarChange}
                              composerPrefill={socialComposerPrefill}
                              composerPrefillKey={socialComposerPrefillKey}
                            />
                          ) : (
                            <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-8 text-center">
                              <Text size="sm" color="muted">
                                Você não tem permissão para acessar o Feed.
                              </Text>
                            </div>
                          )}
                        </TabPanel>

                        {hasCacaPalavrasGames && (
                          <TabPanel className="outline-none focus:outline-none data-[focus]:outline-none">
                            <HomeCacaPalavrasSection onShareResult={handleShareGameResult} />
                          </TabPanel>
                        )}
                      </TabPanels>
                    </TabGroup>
                  </div>
                </>
              )}
            </div>
          </PermissionGuard>
      </div>
      {/* Modal de PDF */}
      <PdfModal
        isOpen={showPdfModal}
        onClose={() => {
          setShowPdfModal(false)
          setPdfUrl(null)
          setPdfTitle('Visualizar arquivo')
        }}
        pdfUrl={pdfUrl}
        title={pdfTitle}
        large={pdfModalLarge}
      />

      {/* Modal de imagem */}
      {showImageModal && imageUrl && (
        <div
          className={clsx(
            'fixed inset-0 bg-black bg-opacity-70 z-[60] flex items-center justify-center',
            pdfModalLarge ? 'p-2 sm:p-3' : 'p-4'
          )}
          onClick={() => {
            setShowImageModal(false)
            setImageUrl(null)
            setImageTitle('Visualizar imagem')
          }}
        >
          <div
            className={clsx(
              'bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full flex flex-col',
              pdfModalLarge ? 'max-w-[96vw] h-[96vh]' : 'max-w-5xl max-h-[90vh]'
            )}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
              <Heading as="h2" size="2xl" className="text-gray-900 dark:text-white truncate pr-4">
                {imageTitle}
              </Heading>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setShowImageModal(false)
                  setImageUrl(null)
                  setImageTitle('Visualizar imagem')
                }}
                className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                aria-label="Fechar modal"
              >
                <XMarkIcon className="h-5 w-5" />
              </Button>
            </div>
            <div className="flex-1 overflow-auto p-4 flex items-center justify-center bg-gray-50 dark:bg-gray-900">
              <img
                src={imageUrl}
                alt={imageTitle}
                className={clsx(
                  'max-w-full object-contain',
                  pdfModalLarge ? 'max-h-[88vh]' : 'max-h-[75vh]'
                )}
              />
            </div>
          </div>
        </div>
      )}
      
      {/* Modal de detalhe (Interno / Boletim) */}
      {showDetailModal && selectedDetailVersion && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
          onClick={() => {
            setShowDetailModal(false)
            setSelectedDetailVersion(null)
          }}
        >
          <div 
            className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto focus:outline-none"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700 sticky top-0 z-10 bg-white dark:bg-gray-800">
              <div className="flex-1 flex items-center gap-4 flex-wrap">
                <Heading as="h2" size="2xl" className="text-gray-900 dark:text-white">
                  {selectedDetailVersion.title}
                </Heading>
                <div className="flex items-center gap-1 text-sm text-gray-500 dark:text-gray-400">
                  <CalendarIcon className="h-4 w-4" />
                  {new Date(selectedDetailVersion.date).toLocaleDateString('pt-BR', {
                    day: '2-digit',
                    month: '2-digit',
                    year: 'numeric'
                  })}
                </div>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setShowDetailModal(false)
                  setSelectedDetailVersion(null)
                }}
                className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                aria-label="Fechar modal"
              >
                <XMarkIcon className="h-5 w-5" />
              </Button>
            </div>

            {/* Conteúdo */}
            <div className="p-6">
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Descrição
                </label>
                <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 border border-gray-200 dark:border-gray-600">
                  <div
                    className="prose dark:prose-invert prose-sm max-w-none text-gray-900 dark:text-white"
                    dangerouslySetInnerHTML={{
                      __html: formatDescription(selectedDetailVersion.description || '')
                    }}
                  />
                </div>
              </div>
              
              {selectedDetailVersion.file && detailKind === 'interno' && (
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Arquivo
                  </label>
                  {isImageFilePath(selectedDetailVersion.file) && getFileUrl(selectedDetailVersion.file) ? (
                    <button
                      type="button"
                      onClick={() => openAttachedFile(selectedDetailVersion.file, selectedDetailVersion.title)}
                      className="block w-full text-left"
                    >
                      <img
                        src={getFileUrl(selectedDetailVersion.file)!}
                        alt={selectedDetailVersion.title}
                        className="max-h-96 w-auto max-w-full rounded-lg border border-gray-200 dark:border-gray-600 object-contain bg-gray-50 dark:bg-gray-700"
                      />
                    </button>
                  ) : (
                    <Button
                      onClick={() => openAttachedFile(selectedDetailVersion.file, selectedDetailVersion.title)}
                      className="flex items-center gap-2"
                      style={{ backgroundColor: BRAND_COLOR }}
                    >
                      <DocumentTextIcon className="h-5 w-5" />
                      Visualizar Arquivo
                    </Button>
                  )}
                </div>
              )}

              {selectedDetailVersion.link && (
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Link
                  </label>
                  <Button
                    onClick={() => {
                      const normalizedLink = normalizeUrl(selectedDetailVersion.link!)
                      window.open(normalizedLink, '_blank')
                    }}
                    variant="outline"
                    className="flex items-center gap-2"
                  >
                    <DocumentTextIcon className="h-5 w-5" />
                    Abrir Link
                  </Button>
                </div>
              )}
              
              <div className="flex justify-end">
                <Button
                  variant="ghost"
                  onClick={() => {
                    setShowDetailModal(false)
                    setSelectedDetailVersion(null)
                  }}
                >
                  Fechar
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Modal de Notificação Completa - mesmo modal do menu Notificações (sidebar) */}
      {selectedNotification && (
        <NotificationDetailModal
          notification={selectedNotification}
          senderInfo={senderInfo}
          loadingAvatar={loadingAvatar}
          onClose={() => setSelectedNotification(null)}
          onMarkAsRead={async (id) => {
            await markAsRead(id)
            setSelectedNotification(null)
          }}
          onDelete={async (id) => {
            await deleteOne(id)
            setSelectedNotification(null)
          }}
          onInternalNavigate={handleNotificationInternalNavigate}
        />
      )}

      <UserConfigModal
        isOpen={isUserConfigModalOpen}
        onClose={() => setIsUserConfigModalOpen(false)}
      />

      <QGNumerosModal
        isOpen={isQgNumerosModalOpen && canViewQgNumeros}
        onClose={() => setIsQgNumerosModalOpen(false)}
        data={qgNumerosData}
        loading={qgNumerosLoading}
        error={qgNumerosError}
      />

      <HomePopup />
    </>
  )
}

export default Dashboard